import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  checkForGuestCheckoutCookie,
  deleteGuestCheckoutCookie,
} from '@insight/toolkit-utils/lib/helpers/cookieHelpers';
import { getCurrentLocale, isEmeaRegion } from '@insight/toolkit-utils';
import { getShoppingRequestEnabledFlag } from '@insight/toolkit-utils/lib/helpers/getShoppingRequestEnabledFlag';
import AddToCartAccessoryModal from './AddToCartAccessoryModal';
import AddToCartWarrantyModal from './AddToCartWarrantyModal';
import AddToCartCarrierModal from './AddToCartCarrierModal';

export const AddToCartModal = ({
  parentId,
  currencyCode,
  isLoggedIn,
  isOpen,
  products,
  onClose,
  enabledWarranty,
  accessories,
  protection,
  thirdPartyProtection,
  availableCarriers,
  quantity = 1,
  stock,
  addToCart,
  showQuantityExceed = false,
  isShoppingReqWGEnabled,
  permissions,
  showDividers,
  showPrice,
  asyncPrice,
  richRelevanceSSEComplete,
  selectedContract,
  isIPSUserWithContract,
  carriersContactFormLink,
  categoryType,
  isPerformanceFlagEnabledAppWise = false,
}) => {
  if (!isOpen || !products?.length) return null;

  const flatPermissions = {
    enable_warranty_purchase:
      permissions.enable_warranty_purchase || permissions.enableWarranties,
    enable_accessory_purchase:
      permissions.enable_accessory_purchase || permissions.enableAccessories,
    enable_purchasing_popup:
      permissions.enable_purchasing_popup || permissions.enablePurchasePopup,
  };

  const isPerformanceFlagEnabled =
    window?.flags && window?.flags['GNA-16270-ADD-TO-CART-PER'];

  const locale = getCurrentLocale('insight_locale');
  const isLoggedOutDefault =
    window?.flags &&
    window?.flags['GNA-10394-SHOPPING-CART'] &&
    !isLoggedIn &&
    locale.split('_')[1] === 'US';
  const isUSLocale = locale?.split('_')?.[1] === 'US';
  const isCALocale = locale?.split('_')?.[1] === 'CA';
  const isShoppingCartEnabled =
    window?.flags &&
    getShoppingRequestEnabledFlag(
      locale,
      isLoggedOutDefault,
      isShoppingReqWGEnabled
    );
  const carriersNotAvailable =
    !availableCarriers?.length ||
    isLoggedOutDefault ||
    !(isUSLocale || isCALocale) ||
    (window?.flags && !window?.flags['GNA-13803-CELLULAR-CARRIER-ACTIVATION']);
  const protectionNotAvailable =
    !!enabledWarranty ||
    (!protection?.prodList?.length && !thirdPartyProtection?.length) ||
    !flatPermissions?.enable_warranty_purchase;

  const [currentModal, setCurrentModal] = useState('carrier');
  const [isProtectionClick, setIsProtectionClick] = useState(false);
  const [isCarrierClick, setIsCarrierClick] = useState(false);

  const initialSelectedWarranty = enabledWarranty
    ? {}
    : protection?.prodList?.length > 0
      ? {
          ...protection?.prodList[0],
          contractID: asyncPrice
            ? asyncPrice[protection?.prodList[0]?.materialId]?.contractNumber
            : null,
          hasAvilablePrice: isIPSUserWithContract
            ? !!asyncPrice[protection?.prodList[0]?.materialId]
            : true,
          callForPrice:
            isIPSUserWithContract && !!asyncPrice
              ? asyncPrice[protection?.prodList[0]?.materialId]?.callForPrice
              : false,
        }
      : thirdPartyProtection?.length > 0
        ? thirdPartyProtection[0]
        : {};

  const calculateAvailableWarranty = (protections) => {
    if (protections?.length === 0) return false;

    protections.every((protection) => {
      if (!asyncPrice[protection?.materialId]) return false;
      !asyncPrice[protection?.materialId]?.callForPrice;
    });
  };

  const hasAvailableWarranty = () => {
    if (!!protection?.prodList?.length || !!thirdPartyProtection?.length) {
      return isIPSUserWithContract
        ? calculateAvailableWarranty([
            ...protection?.prodList,
            ...thirdPartyProtection,
          ])
        : true;
    }
    return true;
  };

  const [selectedWarranty, setSelectedWarranty] = useState({});
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const showVAT = isEmeaRegion();

  useEffect(() => {
    setSelectedWarranty({});
    if (isOpen && flatPermissions.enable_warranty_purchase) {
      setSelectedWarranty(initialSelectedWarranty);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isPerformanceFlagEnabled && isPerformanceFlagEnabledAppWise) {
      if (isOpen) {
        if (!carriersNotAvailable && !isCarrierClick) {
          setCurrentModal('carrier');
          setIsCarrierClick(false);
        } else if (richRelevanceSSEComplete && carriersNotAvailable) {
          const modal =
            isProtectionClick && accessories?.prodList.length
              ? 'accessories'
              : protectionNotAvailable
                ? 'accessories'
                : 'protection';
          setCurrentModal(modal);
          setIsProtectionClick(false);
        } else {
          setCurrentModal('protection');
        }
      } else {
        setTimeout(() => {
          setCurrentModal('carrier');
          if (carriersNotAvailable) {
            setCurrentModal('protection');
          }
        }, 1000);
      }
    } else {
      setCurrentModal('carrier');

      if (isOpen) {
        if (carriersNotAvailable) {
          setCurrentModal(
            protectionNotAvailable ? 'accessories' : 'protection'
          );
        }
      }
    }
  }, [
    enabledWarranty,
    protection,
    thirdPartyProtection,
    isOpen,
    availableCarriers,
    richRelevanceSSEComplete,
  ]);
  const trackSkipCarrier = () => {
    if (!!carriersNotAvailable || !!selectedCarrier) {
      return;
    }
    const {
      materialId,
      price,
      category,
      description,
      descriptions,
      manufacturerName,
      manufacturer,
    } = products[0];
    const productSKU = {
      name: description || descriptions?.shortDescription,
      productSku: materialId,
      insightPartId: materialId,
      price: price?.webPrice,
      brand: manufacturerName || manufacturer?.name,
      category: category?.code,
      quantity,
      currency: currencyCode,
      categoryType,
      carrier: 'No selected carrier',
    };
    window.fireTagEvent('skipCarrierActivation', { product: productSKU });
  };
  const guestCheckoutCookieHandler = () => {
    if (isLoggedOutDefault) {
      checkForGuestCheckoutCookie('guest-checkout-enabled');
    } else {
      deleteGuestCheckoutCookie('guest-checkout-enabled');
      trackSkipCarrier();
      window.location = isShoppingCartEnabled
        ? '/insightweb/cart'
        : '/insightweb/viewCart';
    }
  };

  const hasWarranties =
    !!protection?.prodList?.length ||
    !!thirdPartyProtection?.length ||
    hasAvailableWarranty();
  const onCloseModal = () => {
    trackSkipCarrier();
    onClose();
  };
  return (
    <>
      {currentModal === 'carrier' && (
        <AddToCartCarrierModal
          parentId={parentId}
          availableCarriers={availableCarriers || []}
          products={products}
          addToCart={addToCart}
          setCurrentModal={setCurrentModal}
          selectedWarranty={selectedWarranty}
          setSelectedWarranty={setSelectedWarranty}
          isLoggedIn={isLoggedIn}
          categoryType={categoryType}
          currencyCode={currencyCode}
          onClose={onCloseModal}
          protectionNotAvailable={protectionNotAvailable}
          selectedCarrier={selectedCarrier}
          setSelectedCarrier={setSelectedCarrier}
          carriersContactFormLink={carriersContactFormLink}
          richRelevanceSSEComplete={richRelevanceSSEComplete}
          setIsCarrierClick={setIsCarrierClick}
        />
      )}
      {currentModal === 'protection' && (
        <AddToCartWarrantyModal
          protection={protection?.prodList || []}
          thirdPartyProtection={thirdPartyProtection || []}
          products={products}
          addToCart={addToCart}
          setCurrentModal={setCurrentModal}
          selectedWarranty={selectedWarranty}
          setSelectedWarranty={setSelectedWarranty}
          isLoggedIn={isLoggedIn}
          permissions={flatPermissions}
          showPrice={showPrice}
          categoryType={'Protection Plans'}
          showVAT={showVAT}
          asyncPrice={asyncPrice}
          richRelevanceSSEComplete={richRelevanceSSEComplete}
          isIPSUserWithContract={isIPSUserWithContract}
          carriersNotAvailable={carriersNotAvailable}
          selectedCarrier={selectedCarrier}
          setIsProtectionClick={setIsProtectionClick}
        />
      )}
      {currentModal === 'accessories' && (
        <AddToCartAccessoryModal
          products={products}
          quantity={quantity}
          accessories={accessories?.prodList || []}
          onClose={onCloseModal}
          addToCart={addToCart}
          continueCheckoutHandler={guestCheckoutCookieHandler}
          currencyCode={currencyCode}
          stock={stock}
          permissions={flatPermissions}
          showQuantityExceed={showQuantityExceed}
          selectedWarranty={selectedWarranty}
          enabledWarranty={enabledWarranty}
          selectedCarrier={selectedCarrier}
          hasWarranties={hasWarranties}
          showDividers={showDividers}
          showPrice={showPrice}
          categoryType={'Accessories'}
          showVAT={showVAT}
          asyncPrice={asyncPrice}
          selectedContract={selectedContract}
          richRelevanceSSEComplete={richRelevanceSSEComplete}
          isIPSUserWithContract={isIPSUserWithContract}
        />
      )}
    </>
  );
};

export default AddToCartModal;

AddToCartModal.propTypes = {
  currencyCode: PropTypes.string.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isShoppingReqWGEnabled: PropTypes.bool.isRequired,
  products: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  enabledWarranty: PropTypes.bool,
  accessories: PropTypes.object,
  protection: PropTypes.object,
  thirdPartyProtection: PropTypes.array,
  quantity: PropTypes.number,
  stock: PropTypes.number,
  addToCart: PropTypes.func.isRequired,
  showQuantityExceed: PropTypes.bool,
  permissions: PropTypes.object,
  showDividers: PropTypes.bool,
  showPrice: PropTypes.bool,
};

AddToCartModal.defaultProps = {
  hideTabs: false,
  showDividers: false,
  enabledWarranty: false,
  accessories: [],
  protection: [],
  thirdPartyProtection: [],
  quantity: 1,
  permissions: {
    enable_warranty_purchase: true,
    enable_accessory_purchase: true,
    enable_purchasing_popup: true,
  },
  showPrice: true,
};
